import { useCallback, useEffect, useRef } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import "./confirmDialog.css";

const TITLE_ID = "wx-confirm-title";
const BODY_ID = "wx-confirm-body";

/**
 * The shared confirmation dialog.
 *
 * Owner: "confirm ก่อนลบด้วยครับ dialog ไม่เอาแบบ alert ของ javascript นะครับ
 * เอางาน enterprise".
 *
 * `window.confirm` was never used in this product, but until now every screen
 * that needed a confirmation rolled its own inline one (the Buddy Guarantee
 * revoke row, the Job Builder's close guard, the worker profile's unsaved-changes
 * question). This is the one they should all have been: a real dialog with a
 * real title, the thing being destroyed named in full, and a cancel that is the
 * easy path.
 *
 * What makes it safe rather than merely pretty:
 *
 *  - `role="alertdialog"` and `aria-describedby`, so a screen reader announces
 *    WHAT is about to happen rather than just that a dialog opened.
 *  - Focus lands on CANCEL, never on the destructive button. A confirmation whose
 *    dangerous option is one Enter away is not a confirmation.
 *  - Escape cancels, and consumes the event on the capture phase, because this
 *    opens on top of dialogs and drawers that close on Escape themselves - one
 *    press must dismiss the question, not the screen behind it.
 *  - The confirm button shows its own busy state and both buttons disable while
 *    the action runs, so a slow delete cannot be fired twice.
 *  - It renders nothing at all when closed, so no stale question can be revealed
 *    by a CSS change.
 */
export default function ConfirmDialog({
  open,
  title,
  body,
  detail,
  confirmLabel = "ยืนยัน",
  cancelLabel = "ยกเลิก",
  tone = "danger",
  busy = false,
  error = null,
  onConfirm,
  onCancel,
}) {
  const cancelRef = useRef(null);

  const requestCancel = useCallback(() => {
    if (!busy) {
      onCancel?.();
    }
  }, [busy, onCancel]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key !== "Escape") {
        return;
      }
      // Capture phase + stopImmediatePropagation: this dialog opens over
      // surfaces that close on Escape themselves. Without this, answering the
      // question also dismissed whatever asked it.
      event.stopImmediatePropagation();
      requestCancel();
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [open, requestCancel]);

  useEffect(() => {
    if (open) {
      cancelRef.current?.focus();
    }
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <div className="wx-confirm-root">
      <div className="wx-confirm-backdrop" aria-hidden="true" />
      <div
        className={`wx-confirm wx-confirm--${tone}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        aria-describedby={BODY_ID}
      >
        <div className="wx-confirm-head">
          <span className="wx-confirm-mark" aria-hidden="true">
            <AlertTriangle size={19} />
          </span>
          <h2 id={TITLE_ID} className="wx-confirm-title">
            {title}
          </h2>
        </div>

        <div id={BODY_ID} className="wx-confirm-body">
          {body}
        </div>

        {/* What exactly is being destroyed, named. A confirmation that says only
            "are you sure?" is a click-through, not a decision. */}
        {detail && <div className="wx-confirm-detail">{detail}</div>}

        {error && (
          <p className="wx-confirm-error" role="alert">
            {error}
          </p>
        )}

        <div className="wx-confirm-actions">
          <button type="button" ref={cancelRef} className="wx-button wx-button--ghost" disabled={busy} onClick={requestCancel}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`wx-button ${tone === "danger" ? "wx-button--danger" : "wx-button--primary"}`}
            disabled={busy}
            onClick={onConfirm}
          >
            {busy && <Loader2 size={14} aria-hidden="true" className="wx-confirm-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
